import { z } from "zod";
import { ABSENCE_KINDS, TIME_ENTRY_STATUSES, WARNING_CODES } from "../enums.js";
import { ENTRY_ERROR_CODES, MAX_COMMENT_LENGTH } from "../rules/time-entry.js";
import { inboxAbsencesSchema } from "./absences.js";
import { assignmentSummarySchema, employeeSummarySchema } from "./auth.js";
import { idSchema, isoDateSchema, isoTimestampSchema, isoWeekSchema } from "./common.js";
import { syncProblemSchema } from "./sync.js";

// --- request bodies ---------------------------------------------------------------------------

export const breakBodySchema = z.object({
  startedAt: isoTimestampSchema,
  endedAt: isoTimestampSchema,
});
export type BreakBody = z.infer<typeof breakBodySchema>;

export const MAX_BREAKS = 10;

// Create/update body of an entry. `clientId` makes a retried POST idempotent (offline queue).
export const timeEntryBodySchema = z.object({
  assignmentId: idSchema,
  workDate: isoDateSchema,
  startedAt: isoTimestampSchema,
  endedAt: isoTimestampSchema,
  breaks: z.array(breakBodySchema).max(MAX_BREAKS).default([]),
  comment: z.string().trim().max(MAX_COMMENT_LENGTH).nullable().optional(),
  clientId: z.uuid().optional(),
});
export type TimeEntryBody = z.infer<typeof timeEntryBodySchema>;
export type TimeEntryBodyInput = z.input<typeof timeEntryBodySchema>;

export const timeEntryIdParamsSchema = z.object({ id: idSchema });

export const weekQuerySchema = z.object({ week: isoWeekSchema.optional() });
export type WeekQuery = z.infer<typeof weekQuerySchema>;

export const submitWeekBodySchema = z.object({ week: isoWeekSchema });
export type SubmitWeekBody = z.infer<typeof submitWeekBodySchema>;

// Employee correction of a rejected entry: optional changes applied to the copy at once.
export const correctEntryBodySchema = timeEntryBodySchema.omit({ clientId: true }).partial();
export type CorrectEntryBody = z.infer<typeof correctEntryBodySchema>;

// --- responses --------------------------------------------------------------------------------

export const timeBreakSchema = z.object({
  id: z.string(),
  startedAt: isoTimestampSchema,
  endedAt: isoTimestampSchema,
});
export type TimeBreak = z.infer<typeof timeBreakSchema>;

export const timeEntrySchema = z.object({
  id: z.string(),
  zvEmployeeId: z.number().int(),
  assignmentId: z.string(),
  assignment: z.object({ customerName: z.string(), positionLabel: z.string() }).nullable(),
  workDate: isoDateSchema,
  startedAt: isoTimestampSchema,
  endedAt: isoTimestampSchema,
  breaks: z.array(timeBreakSchema),
  comment: z.string().nullable(),
  status: z.enum(TIME_ENTRY_STATUSES),
  warnings: z.array(z.enum(WARNING_CODES)),
  assignmentChanged: z.boolean(),
  rejectionNote: z.string().nullable(),
  // The correcting row (this row is hidden from lists) / the row this one corrected.
  supersededBy: z.string().nullable(),
  supersedes: z.string().nullable(),
  correctedInZvoove: z.boolean(),
  syncPendingDelete: z.boolean(),
  syncError: z.string().nullable(),
  hasBookings: z.boolean(),
  // Read-back (B1) saw zvoove move the bookings to time data; corrections then need zvoove.
  zvProcessed: z.boolean(),
  // A failed outbound job carries this row: "Erneut senden" re-queues it (B1).
  retryable: z.boolean(),
  grossMinutes: z.number().int(),
  breakMinutes: z.number().int(),
  netMinutes: z.number().int(),
  createdBy: z.string(),
  createdByName: z.string().nullable(),
  createdAt: isoTimestampSchema,
  submittedAt: isoTimestampSchema.nullable(),
  decidedBy: z.string().nullable(),
  decidedByName: z.string().nullable(),
  decidedAt: isoTimestampSchema.nullable(),
  updatedAt: isoTimestampSchema,
  clientId: z.string().nullable(),
});
export type TimeEntry = z.infer<typeof timeEntrySchema>;

// An entry with the chain of rows it corrected, newest first.
export const timeEntryDetailSchema = timeEntrySchema.extend({
  history: z.array(timeEntrySchema),
});
export type TimeEntryDetail = z.infer<typeof timeEntryDetailSchema>;

export const dayAbsenceSchema = z.object({
  kind: z.enum([...ABSENCE_KINDS, "other"]),
  label: z.string().nullable(),
});
export type DayAbsence = z.infer<typeof dayAbsenceSchema>;

export const weekDaySchema = z.object({
  date: isoDateSchema,
  holiday: z.string().nullable(),
  absence: dayAbsenceSchema.nullable(),
  // Target minutes of the assignments active on the day.
  assignmentMinutes: z.number().int(),
  // Assignments active on the day (ids of `assignments` below); empty → no entry possible.
  assignmentIds: z.array(z.string()),
  // Inside the work-date window and an assignment is active.
  canAdd: z.boolean(),
  entries: z.array(timeEntryDetailSchema),
});
export type WeekDay = z.infer<typeof weekDaySchema>;

export const weekResponseSchema = z.object({
  week: z.object({ id: isoWeekSchema, start: isoDateSchema, end: isoDateSchema }),
  // The server's Berlin date; the web derives the work-date window and "Heute" from it.
  today: isoDateSchema,
  employee: employeeSummarySchema.nullable(),
  assignments: z.array(assignmentSummarySchema),
  days: z.array(weekDaySchema),
  totals: z.object({ netMinutes: z.number().int(), targetMinutes: z.number().int() }),
  counts: z.object({
    draft: z.number().int(),
    submitted: z.number().int(),
    rejected: z.number().int(),
    approved: z.number().int(),
    warnings: z.number().int(),
  }),
  canSubmit: z.boolean(),
});
export type WeekResponse = z.infer<typeof weekResponseSchema>;

// Drafts that fail the rules at submission are skipped and reported, not the whole week.
export const submitBlockSchema = z.object({
  id: z.string(),
  workDate: isoDateSchema,
  errors: z.record(z.string(), z.enum(ENTRY_ERROR_CODES)),
});
export type SubmitBlock = z.infer<typeof submitBlockSchema>;

export const submitWeekResponseSchema = z.object({
  submitted: z.number().int(),
  skipped: z.array(submitBlockSchema),
});
export type SubmitWeekResponse = z.infer<typeof submitWeekResponseSchema>;

// --- dispatcher -------------------------------------------------------------------------------

export const dispatchWeekQuerySchema = z.object({
  employeeId: z.coerce.number().int().positive(),
  week: isoWeekSchema.optional(),
});
export type DispatchWeekQuery = z.infer<typeof dispatchWeekQuerySchema>;

export const rejectEntryBodySchema = z.object({ note: z.string().trim().min(1).max(500) });
export type RejectEntryBody = z.infer<typeof rejectEntryBodySchema>;

export const approveWeekBodySchema = z.object({
  employeeId: z.number().int().positive(),
  week: isoWeekSchema,
});
export type ApproveWeekBody = z.infer<typeof approveWeekBodySchema>;

export const approveWeekResponseSchema = z.object({ approved: z.number().int() });
export type ApproveWeekResponse = z.infer<typeof approveWeekResponseSchema>;

// Dispatcher correction: full entry body plus a reason kept in the audit log.
export const dispatchCorrectBodySchema = timeEntryBodySchema.omit({ clientId: true }).extend({
  reason: z.string().trim().max(200).optional(),
});
export type DispatchCorrectBody = z.infer<typeof dispatchCorrectBodySchema>;

export const markCorrectedBodySchema = z.object({ corrected: z.boolean().default(true) });
export type MarkCorrectedBody = z.infer<typeof markCorrectedBodySchema>;

export const inboxSubmittedWeekSchema = z.object({
  employee: employeeSummarySchema,
  week: isoWeekSchema,
  weekStart: isoDateSchema,
  weekEnd: isoDateSchema,
  entryCount: z.number().int(),
  netMinutes: z.number().int(),
  warningCount: z.number().int(),
  // Latest submission in the week.
  submittedAt: isoTimestampSchema.nullable(),
});
export type InboxSubmittedWeek = z.infer<typeof inboxSubmittedWeekSchema>;

// A3 (absences) and B1 (sync) extend this shape additively.
export const inboxResponseSchema = inboxAbsencesSchema.extend({
  submittedWeeks: z.array(inboxSubmittedWeekSchema),
  // Failed jobs plus live entries/requests zvoove rejected (B1); `syncErrorItems` are the newest.
  syncErrors: z.number().int(),
  syncErrorItems: z.array(syncProblemSchema),
});
export type InboxResponse = z.infer<typeof inboxResponseSchema>;
